import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Edit, Trophy, Users, Award, Building } from 'lucide-react';

const EditStatsForm = ({ stats, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    founded: stats?.founded || '',
    members: stats?.members || 0,
    championships: stats?.championships || 0,
    facilities: stats?.facilities || ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="space-y-5">
      {/* Founded Year */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <Building className="w-4 h-4 text-muted-foreground" />
          Founded Year
        </Label>
        <Input
          type="number"
          className="input-premium"
          value={formData.founded}
          onChange={(e) => handleChange('founded', parseInt(e.target.value) || 0)}
          placeholder="e.g., 1990"
        />
      </div>

      {/* Total Members */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          Total Members
        </Label>
        <Input
          type="number"
          className="input-premium"
          value={formData.members}
          onChange={(e) => handleChange('members', parseInt(e.target.value) || 0)}
          placeholder="e.g., 150"
        />
      </div>

      {/* Championships Won */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <Trophy className="w-4 h-4 text-muted-foreground" />
          Championships Won
        </Label>
        <Input
          type="number"
          className="input-premium"
          value={formData.championships}
          onChange={(e) => handleChange('championships', parseInt(e.target.value) || 0)}
          placeholder="e.g., 5"
        />
      </div>

      {/* Training Facilities */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <Award className="w-4 h-4 text-muted-foreground" />
          Training Facilities
        </Label>
        <Input
          type="text"
          className="input-premium"
          value={formData.facilities}
          onChange={(e) => handleChange('facilities', e.target.value)}
          placeholder="e.g., Stadium, Gym, Swimming Pool"
        />
        <p className="text-xs text-muted-foreground">Separate multiple facilities with commas</p>
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-border">
        <Button variant="outline" onClick={() => onClose(false)} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button 
          className="btn-primary w-full sm:w-auto"
          onClick={handleSubmit}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const EditStatsModal = ({ 
  isOpen, 
  onClose, 
  stats, 
  onSave 
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto mx-4 p-0" data-testid="edit-stats-modal">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 p-6 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <div className="icon-container-purple">
                <Edit className="w-5 h-5 text-primary" />
              </div>
              Edit Club Information
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6">
          {isOpen && (
            <EditStatsForm 
              key={JSON.stringify(stats)} 
              stats={stats} 
              onSave={onSave} 
              onClose={onClose} 
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditStatsModal;
